import { computed } from 'vue'
import { getReconciliationIndicators, summarizeLotterySales } from '../utils/financialUi.js'

export const useFinancialSummary = ({ lotteries = computed(() => []), lifecycleFilter = computed(() => 'all') } = {}) => {
  const normalizedRows = computed(() => {
    return (lotteries.value || []).map((lottery) => summarizeLotterySales(lottery, {
      ticketPrice: lottery?.ticket_price ?? 0,
    }))
  })

  const lifecycleFilteredRows = computed(() => {
    const filter = String(lifecycleFilter.value || 'all')
    if (filter === 'all') return normalizedRows.value

    if (filter === 'deleted') {
      return normalizedRows.value.filter((row) => row.lifecycle === 'deleted')
    }

    return normalizedRows.value.filter((row) => row.lifecycle !== 'deleted')
  })

  const totals = computed(() => {
    return lifecycleFilteredRows.value.reduce((acc, row) => {
      acc.totalTickets += row.totalTickets
      acc.soldTickets += row.soldTickets
      acc.reservedTickets += row.reservedTickets
      acc.refundedTickets += row.refundedTickets
      acc.voidedTickets += row.voidedTickets
      acc.estimatedRevenue += row.estimatedRevenue
      acc.mismatchCount += row.mismatchCount
      acc.partial = acc.partial || row.partial
      acc.degraded = acc.degraded || row.degraded
      return acc
    }, {
      totalTickets: 0,
      soldTickets: 0,
      reservedTickets: 0,
      refundedTickets: 0,
      voidedTickets: 0,
      estimatedRevenue: 0,
      mismatchCount: 0,
      partial: false,
      degraded: false,
    })
  })

  const indicator = computed(() => getReconciliationIndicators(totals.value))

  const warningSignals = computed(() => {
    const signals = []
    if (indicator.value.hasMismatch) {
      signals.push({
        severity: 'warning',
        source: 'reconciliation',
        recoverable: true,
        retryable: true,
        correlation_ref: '',
        message: `Mismatch detected (${totals.value.mismatchCount}).`,
      })
    }

    if (indicator.value.partial || indicator.value.degraded) {
      signals.push({
        severity: 'info',
        source: 'degraded_data',
        recoverable: true,
        retryable: true,
        correlation_ref: '',
        message: 'Partial/degraded payload detected. Refresh and verify before operational decisions.',
      })
    }

    return signals
  })

  const sellerSummaries = computed(() => {
    const grouped = new Map()

    for (const row of lifecycleFilteredRows.value) {
      const sellerId = Number(row?.seller_id ?? row?.sellerId ?? 0)
      if (!Number.isInteger(sellerId) || sellerId <= 0) continue

      const current = grouped.get(sellerId) || {
        sellerId,
        assignedTickets: 0,
        soldTickets: 0,
        reservedTickets: 0,
        refundedTickets: 0,
        estimatedSettlement: 0,
        outstandingReconciliation: false,
      }

      current.assignedTickets += row.totalTickets
      current.soldTickets += row.soldTickets
      current.reservedTickets += row.reservedTickets
      current.refundedTickets += row.refundedTickets
      current.estimatedSettlement += Math.max(0, row.soldTickets - row.refundedTickets - row.voidedTickets) * Number(row.ticketPrice || 0)
      current.outstandingReconciliation = current.outstandingReconciliation || row.hasMismatch || row.partial || row.degraded
      grouped.set(sellerId, current)
    }

    return [...grouped.values()]
  })

  return {
    normalizedRows,
    lifecycleFilteredRows,
    totals,
    indicator,
    warningSignals,
    sellerSummaries,
  }
}

